import axios from 'axios';
import queryStringify from 'qs-stringify';
import store from 'store2';
import {requestErrorToast} from '../AppInterface/elements/Toasts/Toasts.js';
import {i18n} from '../AppInterface/lib/i18n.js';
import {isTrueObject} from '../AppInterface/lib/helpers/isTrueObject.js';

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRF-TOKEN';
axios.defaults.xsrfCookieName = 'CSRF-TOKEN';
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

class Request {
    constructor() {
        this.axios = axios.create({
            timeout: 30000,
            xsrfHeaderName: 'X-CSRF-TOKEN',
            xsrfCookieName: 'CSRF-TOKEN',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        this.axios.interceptors.response.use(
            response => response,
            error => {
                if (!error.response) {
                    requestErrorToast(i18n.t('errors.serverTimeout', {ns: 'common'}));
                    return;
                } else if (error.response.status) {

                    //todo: second condition just for testing, remove later
                    if (error.response.status === 401 && !error.config.url.includes('/reports/')) {
                        window.location.href = '/';
                        return;
                    } else if (error.response.status === 403) {
                        requestErrorToast(i18n.t('errors.noAccess', {ns: 'common'}));
                    } else if (error.response.status === 404) {
                        requestErrorToast(i18n.t('errors.NotFound', {ns: 'common'}));
                    }
                        // 	500 Internal Server Error
                        // 	501 Not Implemented
                        // 	502 Bad Gateway
                        // 	503 Service Unavailable
                        // 	504 Gateway Timeout
                        // 	505 HTTP Version Not Supported
                        // 	506 Variant Also Negotiates (RFC 2295)
                        // 	507 Insufficient Storage (WebDAV; RFC 4918)
                        // 	508 Loop Detected (WebDAV; RFC 5842)
                        // 	510 Not Extended (RFC 2774)
                    // 511 Network Authentication Required (RFC 6585)

                    else if (error.response.status >= 500 && error.response.status < 600) {
                        if (error.response.status == 502) {
                            requestErrorToast(i18n.t('errors.serverBadGateway', {ns: 'common'}));
                        } else if (error.response.status == 504) {
                            requestErrorToast(i18n.t('errors.serverTimeout', {ns: 'common'}));
                        } else {
                            requestErrorToast(i18n.t('errors.serverError', {ns: 'common'}));
                        }
                    }
                } else if (error.response) {
                    requestErrorToast(i18n.t('errors.serverError', {ns: 'common'}));
                }

                return Promise.reject(error);
            });
    }

    get(url, data, options) {
        return new Promise((resolve, reject) => {
            if (isTrueObject(data)) {
                let queryString = queryStringify(data);
                url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
            }
            this.axios.get(url, options)
                .then(response => {
                    this.storeAppValues(response.headers);
                    resolve(response.data);
                })
                .catch(({response}) => reject(this.getRequestErrorResult(response)));
        });
    }


    getResponse(url, data, options) {
        return new Promise((resolve, reject) => {
            if (isTrueObject(data)) {
                let queryString;
                if (data instanceof URLSearchParams) {
                    queryString = data.toString();
                } else {
                    queryString = queryStringify(data);
                }

                url += url.includes('?') ? `&${queryString}` : `?${queryString}`;

            }
            this.axios.get(url, options)
                .then(response => {
                    this.storeAppValues(response.headers);
                    resolve(response);
                })
                .catch(({response}) => reject(this.getRequestErrorResult(response)));
        });
    }

    post(url, data, options) {
        let payload = data;
        if (isTrueObject(data) && !(data instanceof FormData)) {
            payload = queryStringify(data);
        }
        return new Promise((resolve, reject) => {
            this.axios.post(url, payload, options)
                .then(response => {
                    this.storeAppValues(response.headers);
                    resolve(response.data);
                })
                .catch(({response}) => {
                    reject(this.getRequestErrorResult(response));
                });
        });
    }

    patch(url, data, options) {
        let payload = data;
        if (isTrueObject(data) && !(data instanceof FormData)) {
            payload = queryStringify(data);
        }

        return new Promise((resolve, reject) => {
            this.axios.patch(url, payload, options)
                .then(response => {
                    this.storeAppValues(response.headers);
                    resolve(response.data);
                })
                .catch(({response}) => {
                    reject(this.getRequestErrorResult(response));
                });
        });
    }

    put(url, data, options) {
        let payload = data;
        if (isTrueObject(data) && !(data instanceof FormData)) {
            payload = queryStringify(data);
        }

        return new Promise((resolve, reject) => {
            this.axios.put(url, payload, options)
                .then(response => {
                    this.storeAppValues(response.headers);
                    resolve(response.data);
                })
                .catch(({response}) => {
                    reject(this.getRequestErrorResult(response));
                });
        });
    }

    delete(url, data, options) {
        let payload = data;
        if (isTrueObject(data) && !(data instanceof FormData)) {
            payload = queryStringify(data);
        }
        options = options || {};
        options.data = payload;
        return new Promise((resolve, reject) => {
            this.axios.delete(url, options)
                .then(response => {
                    this.storeAppValues(response.headers);
                    resolve(response.data);
                })
                .catch(({response}) => {
                    reject(this.getRequestErrorResult(response));
                });
        });
    }

    download(url) {
        return new Promise((resolve, reject) => {
            this.axios.get(url, {responseType: 'blob'})
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    const filename = response.headers['content-disposition'].split(';')[1].split('=')[1].replace(/"/g, '');
                    link.href = url;
                    link.download = filename;
                    link.click();
                    resolve(response.data);
                })
                .catch(({response}) => reject(this.getRequestErrorResult(response)));
        });
    }

    //getRequestErrorResult nötig für
    // - Weitergabe der Fehlermeldungen für Frontend, z.B. Forms
    // - Error handling for legacy code (e.g. lp.poll.js)
    getRequestErrorResult(response) {
        let result;
        if (response && response.status) {
            result = {status: response.status, ...response.data};
        } else {
            result = {errors: [response ? 'serverError' : 'serverTimeout']};
        }
        return result;
    }

    storeAppValues(headers) {
        const prefix = 'app-';
        headers.filterKeys(prefix + '*').foreach((value, key) => {
            store.set(key.slice(prefix.length), value);
        });
    }

}

const request = new Request();

const processOptions = options => {
    if (options && options.background) {
        options.headers = options.headers || {};
        options.headers = {...options.headers, Background: 1};
        delete options.background;
    }
    return options;
};

export function post(url, data, options) {
    return request.post(url, data, processOptions(options));
}

export function patch(url, data, options) {
    return request.patch(url, data, processOptions(options));
}

export function put(url, data, options) {
    return request.put(url, data, processOptions(options));
}

export function download(url) {
    return request.download(url);
}

/**
 * @template T
 * @param {string} url
 * @param {Record<string,unknown> | null} [queryParams]
 * @param {axios.AxiosRequestConfig<undefined> | null} [options]
 * @return {Promise<T>}
 */
export function get(url, queryParams, options) {
    return request.get(url, queryParams || null, processOptions(options || null));
}

/**
 * @template T
 * @param url
 * @param { Record<string,unknown> | null } data
 * @param { axios.AxiosRequestConfig<undefined> | null } options
 *
 * @return { Promise<axios.AxiosResponse<T>> }
 */
export function getResponse(url, data, options) {
    return request.getResponse(url, data, processOptions(options));
}

export function del(url, data, options) {
    return request.delete(url, data, options);
}